import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Save, X, Edit2, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  fetchGeoPrompts,
  fetchGeoBrandConfig,
  createGeoPrompt,
  updateGeoPrompt,
  deleteGeoPrompt,
  updateGeoBrandConfig,
  duplicateGeoPromptByCountries,
} from "@/lib/api";

export default function PromptsManager({ clientId }) {
  const qc = useQueryClient();
  const promptsQ = useQuery({
    queryKey: ["geo-prompts", clientId],
    queryFn: () => fetchGeoPrompts(clientId),
  });
  const brandQ = useQuery({
    queryKey: ["geo-brand-config", clientId],
    queryFn: () => fetchGeoBrandConfig(clientId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["geo-prompts", clientId] });
  };

  const prompts = promptsQ.data || [];
  const brand = prompts.filter((p) => p.prompt_type === "brand");
  const category = prompts.filter((p) => p.prompt_type === "category");

  return (
    <div className="space-y-6" data-testid="geo-prompts-manager">
      <BrandConfigCard
        clientId={clientId}
        data={brandQ.data}
        onSaved={() => qc.invalidateQueries({ queryKey: ["geo-brand-config", clientId] })}
      />

      <PromptSection
        title="Prompts sobre la marca"
        subtitle="Cómo te describe la IA, atributos asociados, posibles errores factuales. Usan placeholders [marca] y [descripción corta] que se reemplazan automáticamente."
        items={brand}
        promptType="brand"
        clientId={clientId}
        onChanged={invalidate}
        testid="geo-prompts-brand"
      />
      <PromptSection
        title="Prompts sobre temas / categoría"
        subtitle="A quién nombra la IA en tu vertical, posición media, competidores que aparecen, fuentes que cita."
        items={category}
        promptType="category"
        clientId={clientId}
        onChanged={invalidate}
        testid="geo-prompts-category"
      />
    </div>
  );
}

// ─────── brand config card ───────

function BrandConfigCard({ clientId, data, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [brandName, setBrandName] = useState(data?.brand_name || "");
  const [brandWeb, setBrandWeb] = useState(data?.brand_website || "");
  const [brandDesc, setBrandDesc] = useState(data?.brand_short_desc || "");

  // Re-sync local state when the query resolves or the underlying data changes.
  useEffect(() => {
    if (!editing) {
      setBrandName(data?.brand_name || "");
      setBrandWeb(data?.brand_website || "");
      setBrandDesc(data?.brand_short_desc || "");
    }
  }, [data, editing]);

  const startEdit = () => {
    setBrandName(data?.brand_name || "");
    setBrandWeb(data?.brand_website || "");
    setBrandDesc(data?.brand_short_desc || "");
    setEditing(true);
  };

  const mut = useMutation({
    mutationFn: () =>
      updateGeoBrandConfig(clientId, {
        brand_name: brandName.trim(),
        brand_website: brandWeb.trim(),
        brand_short_desc: brandDesc.trim(),
      }),
    onSuccess: () => {
      toast.success("Configuración de marca guardada");
      setEditing(false);
      onSaved?.();
    },
    onError: () => toast.error("No se pudo guardar"),
  });

  return (
    <section className="rounded-md border border-slate-200 bg-white p-5" data-testid="geo-brand-config-card">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">
            Configuración de marca
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Estos valores reemplazan los placeholders <code className="px-1 bg-slate-100 rounded">[marca]</code>, <code className="px-1 bg-slate-100 rounded">[web]</code> y <code className="px-1 bg-slate-100 rounded">[descripción corta]</code> en los prompts de marca antes de ejecutar el análisis. El <strong>[web]</strong> es clave para que las IAs no confundan tu marca con otras de nombre similar.
          </p>
        </div>
        {!editing && (
          <Button size="sm" variant="outline" onClick={startEdit} className="gap-1">
            <Edit2 className="h-3.5 w-3.5" /> Editar
          </Button>
        )}
      </div>

      {!editing ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <Field label="[marca]" value={data?.brand_name} />
          <Field label="[web]" value={data?.brand_website} />
          <Field label="[descripción corta]" value={data?.brand_short_desc} />
        </div>
      ) : (
        <div className="space-y-3 mt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Nombre de marca</label>
              <Input
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Ej: Bridar"
                data-testid="geo-brand-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Sitio web</label>
              <Input
                value={brandWeb}
                onChange={(e) => setBrandWeb(e.target.value)}
                placeholder="Ej: bridar.com.ar"
                data-testid="geo-brand-web-input"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Descripción corta (una línea)</label>
            <Textarea
              value={brandDesc}
              onChange={(e) => setBrandDesc(e.target.value)}
              rows={2}
              placeholder="Ej: viandas corporativas, comedores in situ y servicios de alimentación para empresas"
              data-testid="geo-brand-desc-input"
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={() => mut.mutate()}
              disabled={mut.isPending || !brandName.trim()}
              className="bg-violet-600 hover:bg-violet-700 text-white gap-1"
              data-testid="geo-brand-save"
            >
              <Save className="h-3.5 w-3.5" />
              Guardar
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
        {label}
      </div>
      <div className="text-sm text-slate-800 mt-0.5">
        {value || <span className="text-slate-400 italic">— sin definir —</span>}
      </div>
    </div>
  );
}

// ─────── prompts section (brand or category) ───────

function PromptSection({ title, subtitle, items, promptType, clientId, onChanged, testid }) {
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState("");

  const createMut = useMutation({
    mutationFn: () =>
      createGeoPrompt(clientId, {
        text: newText.trim(),
        prompt_type: promptType,
      }),
    onSuccess: () => {
      toast.success("Prompt agregado");
      setNewText("");
      setAdding(false);
      onChanged?.();
    },
    onError: () => toast.error("No se pudo crear"),
  });

  return (
    <section data-testid={testid}>
      <div className="flex items-end justify-between mb-2">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">
            {title}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">{subtitle}</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAdding(true)}
          className="gap-1"
          data-testid={`${testid}-add-btn`}
        >
          <Plus className="h-3.5 w-3.5" /> Agregar
        </Button>
      </div>

      <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
        {adding && (
          <div className="p-3 bg-slate-50/50 flex items-center gap-2">
            <Input
              autoFocus
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder={
                promptType === "brand"
                  ? "Ej: ¿Quiénes son los clientes ideales de [marca]?"
                  : "Ej: ¿Mejores proveedores de…?"
              }
              data-testid={`${testid}-new-input`}
            />
            <Button
              size="sm"
              onClick={() => createMut.mutate()}
              disabled={!newText.trim() || createMut.isPending}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid={`${testid}-new-save`}
            >
              <Save className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setAdding(false);
                setNewText("");
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {!items.length && !adding && (
          <div className="p-6 text-sm text-slate-500 text-center">
            No hay prompts de este tipo todavía.
          </div>
        )}

        {items.map((p) => (
          <PromptRow
            key={p.id}
            prompt={p}
            clientId={clientId}
            onChanged={onChanged}
            testidBase={testid}
          />
        ))}
      </div>
    </section>
  );
}

function PromptRow({ prompt, clientId, onChanged, testidBase }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(prompt.text);

  const saveMut = useMutation({
    mutationFn: () =>
      updateGeoPrompt(clientId, prompt.id, {
        text: text.trim(),
        prompt_type: prompt.prompt_type,
      }),
    onSuccess: () => {
      toast.success("Prompt actualizado");
      setEditing(false);
      onChanged?.();
    },
    onError: () => toast.error("No se pudo guardar"),
  });

  const toggleMut = useMutation({
    mutationFn: (newActive) =>
      updateGeoPrompt(clientId, prompt.id, { is_active: newActive }),
    onSuccess: () => {
      onChanged?.();
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteGeoPrompt(clientId, prompt.id),
    onSuccess: () => {
      toast.success("Prompt eliminado");
      onChanged?.();
    },
  });

  const dupMut = useMutation({
    mutationFn: (countries) => duplicateGeoPromptByCountries(clientId, prompt.id, countries),
    onSuccess: (r) => {
      const skipped = r.skipped || 0;
      const created = r.created || 0;
      toast.success(
        `${created} variante${created === 1 ? "" : "s"} creada${created === 1 ? "" : "s"}` +
        (skipped > 0 ? ` · ${skipped} ya existían` : ""),
      );
      setDupOpen(false);
      onChanged?.();
    },
    onError: () => toast.error("No se pudieron crear las variantes"),
  });
  const [dupOpen, setDupOpen] = useState(false);
  const COUNTRIES = ["México", "Argentina", "Colombia", "Perú", "Chile", "Uruguay"];

  return (
    <div className="px-3 py-2.5 flex items-start gap-3" data-testid={`${testidBase}-row`}>
      <div className="shrink-0 pt-1 flex flex-col items-center gap-1">
        <Switch
          checked={prompt.is_active !== false}
          onCheckedChange={(v) => toggleMut.mutate(v)}
          disabled={toggleMut.isPending}
          className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-300"
          data-testid={`${testidBase}-toggle`}
        />
        <span
          className={`text-[9px] uppercase tracking-wider font-semibold ${
            prompt.is_active !== false ? "text-emerald-600" : "text-slate-400"
          }`}
        >
          {toggleMut.isPending
            ? "..."
            : prompt.is_active !== false
            ? "Activo"
            : "Inactivo"}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        {prompt.label && (
          <div className="text-[10px] uppercase tracking-wider text-violet-700 font-medium mb-0.5">
            {prompt.label}
          </div>
        )}
        {!editing ? (
          <div className={`text-sm leading-snug ${prompt.is_active !== false ? "text-slate-800" : "text-slate-400 line-through"}`}>
            {prompt.text}
          </div>
        ) : (
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            autoFocus
          />
        )}
        {prompt.is_seed && (
          <div className="text-[10px] text-slate-400 mt-1 italic">
            Plantilla — al editarla se crea una copia personalizada.
          </div>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-1 relative">
        {!editing ? (
          <>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-slate-500 hover:text-sky-600"
              onClick={() => setDupOpen((v) => !v)}
              title="Duplicar en variantes por país"
              data-testid={`${testidBase}-duplicate-country`}
            >
              <Globe2 className="h-3.5 w-3.5" />
            </Button>
            {dupOpen && (
              <div className="absolute top-8 right-0 bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1 w-52">
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-100">
                  Duplicar variante
                </div>
                {COUNTRIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => dupMut.mutate([c])}
                    disabled={dupMut.isPending}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-sky-50 disabled:opacity-50"
                  >
                    {c}
                  </button>
                ))}
                <div className="border-t border-slate-100 mt-1">
                  <button
                    onClick={() => dupMut.mutate(["México", "Argentina", "Colombia", "Perú"])}
                    disabled={dupMut.isPending}
                    className="w-full text-left px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-50"
                    data-testid={`${testidBase}-duplicate-all-latam4`}
                  >
                    ★ México + Argentina + Colombia + Perú
                  </button>
                </div>
              </div>
            )}
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(true)}>
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-rose-500 hover:text-rose-700"
              onClick={() => {
                if (window.confirm("¿Eliminar este prompt?")) deleteMut.mutate();
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        ) : (
          <>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              <Save className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => {
                setEditing(false);
                setText(prompt.text);
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
